import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./Metrics.module.scss";

import PatientGraphView from "../userInformation/graphView/NextStepGraphView";
import {
  calculateActiveDaysAndTaskCompliance,
  convertDailyMissionsToMetricData,
  convertMissionScoresToMetricData,
  getMetricsMissionIds
} from "../userInformation/NextStepUserInformation.helpers";
import PatientMetrics from "../userInformation/userMetric/NextStepUserMetrics";

import PulseLoader from "~/components/pulseLoader/PulseLoader";
import Tabs from "~/components/tabs/Tabs";
import { useMessageThreads } from "~/hooks/useApi/useMessageThreads";
import useMissions from "~/hooks/useApi/useMissions";
import useMissionSummary from "~/hooks/useApi/useMissionSummary";
import { NextRecommendedUserData } from "~/hooks/useApi/useNextRecommandedUser";
import { usePatientMetrics } from "~/hooks/useApi/usePatientMetrics";
import { UserDetails } from "~/typing/sidekickTypes";

type MetricsProps = {
  nextStepUserInfo: NextRecommendedUserData;
  userDetail: UserDetails;
};
const Metrics = ({ nextStepUserInfo, userDetail }: MetricsProps) => {
  const { t } = useTranslation();

  const [loading, setIsLoading] = useState(false);
  const [activeView, setActiveView] = useState("metrics");

  const { messageThreads } = useMessageThreads({
    userId: nextStepUserInfo.user_id,
    programId: nextStepUserInfo.program_catalog_item_id,
    locale: nextStepUserInfo.locale
  });

  const { missions } = useMissions();

  const { slotDays, isLoading: missionSummaryLoading } = useMissionSummary({
    userId: nextStepUserInfo.user_id,
    programId: nextStepUserInfo.program_catalog_item_id,
    locale: nextStepUserInfo.locale,
    startDate: DateTime.now().minus({ weeks: 2 }).toISODate() ?? "",
    endDate: DateTime.now().toISODate() ?? ""
  });

  const {
    energyMissionId,
    sleepMissionId,
    stressMissionId
  } = getMetricsMissionIds(missions ?? []);

  const {
    sleepScores,
    energyScores,
    stressScores,
    isLoading: metricsLoading
  } = usePatientMetrics({
    userId: nextStepUserInfo.user_id,
    programCatalogItemId: nextStepUserInfo.program_catalog_item_id,
    locale: nextStepUserInfo.locale,
    sleepMissionId,
    energyMissionId,
    stressMissionId
  });

  const metricDataOverLastWeek = useMemo(() => {
    setIsLoading(true);
    const activeDaysMetricData = calculateActiveDaysAndTaskCompliance(
      slotDays,
      messageThreads,
      userDetail
    );
    const dailySummaryMetricData = convertDailyMissionsToMetricData({
      dailySummary: userDetail?.dailySummary
    });

    const missionScoresMetricData = convertMissionScoresToMetricData({
      sleepScores,
      energyScores,
      stressScores
    });
    setIsLoading(false);
    return [
      ...activeDaysMetricData,
      ...dailySummaryMetricData,
      ...missionScoresMetricData
    ];
  }, [
    slotDays.length,
    sleepScores.length,
    energyScores.length,
    stressScores.length,
    userDetail
  ]);

  if (loading || metricsLoading || missionSummaryLoading)
    return (
      <section className={styles.loadingWrapper}>
        <PulseLoader inverted />
      </section>
    );

  return (
    <div className={styles.metricsWrapper}>
      <Tabs
        tabs={[
          {
            title: t("nextStep.userMetrics"),
            value: "metrics",
            onClick: (value) => {
              setActiveView(value);
              // trackNextStepUserMetricsViewed();
            }
          },
          {
            title: t("nextStep.graphView"),
            value: "graph",
            onClick: setActiveView
          }
        ]}
        activeTab={activeView}
      />
      <section>
        {activeView === "graph" ? (
          <PatientGraphView />
        ) : (
          <PatientMetrics metrics={metricDataOverLastWeek} />
        )}
      </section>
    </div>
  );
};

export default Metrics;
